import { MethodIdSchema } from '@dpem/ontology';
import * as z from 'zod';

import { MethodStateSchema, ReviewVerdictSchema } from '../common/enums.js';
import { PageQuerySchema, paginated } from '../common/pagination.js';
import {
  DateRangeFilterSchema,
  DescriptionSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LabelSchema,
  MarkdownSchema,
  RevisionSchema,
  ShortTextSchema,
  TagListSchema,
  TextFilterSchema,
  sortBy,
} from '../common/primitives.js';

/**
 * authoring context — the `MethodVersion` aggregate's lifecycle.
 * Graph mutation lives in `method-graph.ts`; this module is identity + state.
 */

export const METHOD_SORT_FIELDS = ['name', 'updatedAt', 'createdAt', 'state'] as const;

export const MethodVersionSummarySchema = z.strictObject({
  /** Surrogate key of the version record. */
  id: IdentifierSchema,
  /** Canonical ontology id, immutable and shared by every version (`dpem:DPEM`). */
  methodId: MethodIdSchema,
  name: LabelSchema,
  description: DescriptionSchema.nullable(),
  state: MethodStateSchema,
  revision: RevisionSchema,
  versionLabel: ShortTextSchema,
  /** Variants are edges, not types (architecture §3). */
  variantOfId: IdentifierSchema.nullable(),
  tags: TagListSchema,
  nodeCount: z.int().min(0),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  createdBy: ShortTextSchema,
  publishedAt: IsoDateTimeSchema.nullable(),
});
export type MethodVersionSummary = z.infer<typeof MethodVersionSummarySchema>;

/**
 * How many versions were derived from this one, how many workshop sessions run against it,
 * and how many plans were generated from it.
 *
 * These exist for one reason: they are what makes a version undeletable (see
 * `methodDeleteBlockers`), and a list that offered a delete control without them would be
 * offering one the server is going to refuse. Counts rather than booleans so the refusal
 * can say *how many* rather than only *that*.
 *
 * Kept out of `MethodVersionSummarySchema` and mixed into the authoring shapes below
 * instead, because that summary is also the shape publishing's snapshot rows and a
 * workshop's `sourceMethod` are built on, and neither of those reads `method_versions`
 * for the dependants of a version it is only naming. A field they would have to invent a
 * zero for is a field that does not belong to them.
 */
export const MethodVersionDependantsSchema = z.strictObject({
  variantCount: z.int().min(0),
  workshopSessionCount: z.int().min(0),
  planCount: z.int().min(0),
});
export type MethodVersionDependants = z.infer<typeof MethodVersionDependantsSchema>;

/** Why a version cannot be deleted. Empty list means it can. */
export const METHOD_DELETE_BLOCKERS = [
  'PUBLISHED',
  'HAS_VARIANTS',
  'IN_WORKSHOP',
  'HAS_PLANS',
] as const;
export const MethodDeleteBlockerSchema = z.enum(METHOD_DELETE_BLOCKERS);
export type MethodDeleteBlocker = z.infer<typeof MethodDeleteBlockerSchema>;

/**
 * The deletion rule, in the published language, so both sides run the same one.
 *
 * It lives here rather than in either application because the two callers cannot be
 * allowed to disagree: the API enforces it inside the delete transaction, and the web
 * client uses it to decide whether to offer the control at all. Written twice it would
 * drift, and the failure mode of drift is the worst one available — a confirmation dialog
 * that takes a typed `DELETE` and then reports that the delete was impossible all along.
 *
 * The blockers are not a policy invented here; they are the `onDelete: Restrict` foreign
 * keys the schema already carries. A published version is pinned by its `PublishedMethod`
 * snapshot, a source version by the `variantOf` edges pointing at it, a version under
 * adoption by its `WorkshopSession`, and a version somebody has planned against by its
 * `Plan` rows — a work breakdown is a projection of exactly one version, and deleting the
 * version under it would leave a schedule whose tasks name elements that no longer exist.
 * Everything else about a version — its graph, its edges, its catalogue references, its
 * locks — cascades, so nothing else needs asking about.
 */
export const methodDeleteBlockers = (
  version: MethodVersionDependants & { readonly state: MethodVersionSummary['state'] },
): readonly MethodDeleteBlocker[] => {
  const blockers: MethodDeleteBlocker[] = [];
  if (version.state === 'PUBLISHED') blockers.push('PUBLISHED');
  if (version.variantCount > 0) blockers.push('HAS_VARIANTS');
  if (version.workshopSessionCount > 0) blockers.push('IN_WORKSHOP');
  if (version.planCount > 0) blockers.push('HAS_PLANS');
  return blockers;
};

export const MethodVersionCountsSchema = z.strictObject({
  phases: z.int().min(0),
  stages: z.int().min(0),
  workPractices: z.int().min(0),
  activities: z.int().min(0),
  roles: z.int().min(0),
  gates: z.int().min(0),
  lenses: z.int().min(0),
  ceremonies: z.int().min(0),
  deliverables: z.int().min(0),
  edges: z.int().min(0),
});
export type MethodVersionCounts = z.infer<typeof MethodVersionCountsSchema>;

/**
 * A row of the authoring dashboard: the summary, plus what depends on the version.
 *
 * Only this list carries the dependants. Explorer and compare read the same versions
 * through their own contexts' shapes and have nothing to delete.
 */
export const MethodVersionListItemSchema = z.strictObject({
  ...MethodVersionSummarySchema.shape,
  ...MethodVersionDependantsSchema.shape,
});
export type MethodVersionListItem = z.infer<typeof MethodVersionListItemSchema>;

export const MethodVersionDetailSchema = z.strictObject({
  ...MethodVersionSummarySchema.shape,
  ...MethodVersionDependantsSchema.shape,
  writeup: MarkdownSchema.nullable(),
  counts: MethodVersionCountsSchema,
  /** Set while a review is open. */
  submittedAt: IsoDateTimeSchema.nullable(),
  submittedBy: ShortTextSchema.nullable(),
  reviewedAt: IsoDateTimeSchema.nullable(),
  reviewedBy: ShortTextSchema.nullable(),
  reviewComment: DescriptionSchema.nullable(),
});
export type MethodVersionDetail = z.infer<typeof MethodVersionDetailSchema>;

export const ListMethodsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  ...DateRangeFilterSchema.shape,
  ...sortBy(METHOD_SORT_FIELDS).shape,
  state: MethodStateSchema.optional(),
  variantOfId: IdentifierSchema.optional(),
  methodId: MethodIdSchema.optional(),
});
export type ListMethodsRequest = z.infer<typeof ListMethodsRequestSchema>;
export const ListMethodsResponseSchema = paginated(MethodVersionListItemSchema);
export type ListMethodsResponse = z.infer<typeof ListMethodsResponseSchema>;

export const CreateMethodRequestSchema = z.strictObject({
  methodId: MethodIdSchema,
  name: LabelSchema,
  description: DescriptionSchema.optional(),
  writeup: MarkdownSchema.optional(),
  versionLabel: ShortTextSchema.optional(),
  tags: TagListSchema.optional(),
  /** Seed the draft from an existing version's graph instead of an empty one. */
  copyFromId: IdentifierSchema.optional(),
});
export type CreateMethodRequest = z.infer<typeof CreateMethodRequestSchema>;
export const CreateMethodResponseSchema = z.strictObject({ method: MethodVersionDetailSchema });
export type CreateMethodResponse = z.infer<typeof CreateMethodResponseSchema>;

export const GetMethodRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
});
export type GetMethodRequest = z.infer<typeof GetMethodRequestSchema>;
export const GetMethodResponseSchema = z.strictObject({ method: MethodVersionDetailSchema });
export type GetMethodResponse = z.infer<typeof GetMethodResponseSchema>;

/**
 * Editing the version's own name, description, writeup and tags — the method-level
 * counterpart of `UpdateElementRequest`.
 *
 * Its absence was a hole: `CreateMethodRequest` accepts all four and nothing could
 * ever change them, so a typo in a method's name was permanent and the method's own
 * writeup — the overview page of the whole published document — could only be
 * written in the same request that created the draft, before the author had seen
 * the graph they were describing.
 *
 * `null` clears a field and an absent key leaves it alone, exactly as in an element
 * patch. `tags` takes a whole list because that is how a tag editor works; sending
 * `[]` empties it, so no `null` is needed there.
 */
export const UpdateMethodRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  name: LabelSchema.optional(),
  description: DescriptionSchema.nullable().optional(),
  writeup: MarkdownSchema.nullable().optional(),
  tags: TagListSchema.optional(),
});
export type UpdateMethodRequest = z.infer<typeof UpdateMethodRequestSchema>;
export const UpdateMethodResponseSchema = z.strictObject({ method: MethodVersionDetailSchema });
export type UpdateMethodResponse = z.infer<typeof UpdateMethodResponseSchema>;

export const DeriveVariantRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  name: LabelSchema,
  /** A variant keeps element ids so compare joins on them; only the method id differs. */
  methodId: MethodIdSchema,
  description: DescriptionSchema.optional(),
  versionLabel: ShortTextSchema.optional(),
  tags: TagListSchema.optional(),
});
export type DeriveVariantRequest = z.infer<typeof DeriveVariantRequestSchema>;
export const DeriveVariantResponseSchema = z.strictObject({ method: MethodVersionDetailSchema });
export type DeriveVariantResponse = z.infer<typeof DeriveVariantResponseSchema>;

/**
 * Deleting a version outright, as opposed to moving it through the lifecycle.
 *
 * `expectedRevision` is deliberately absent, and it is the one command here that omits
 * it. Every other write replays against a revision because it edits a graph that may
 * have moved underneath the caller; this one removes the graph entirely, so there is no
 * state left to have raced with. Requiring it would only mean an author whose colleague
 * renamed an element has to reload before they are allowed to throw the whole thing away.
 *
 * A version anything else depends on is refused rather than cascaded. What depends on it
 * is a fact the caller needs *before* they open a confirmation, so the counts it turns on
 * ride along on every authoring list row (`MethodVersionDependantsSchema`) and the shared
 * `methodDeleteBlockers` decides from them; this
 * route runs the same rule inside the transaction and refuses with `CONFLICT`, naming the
 * blockers in `details`. A confirmation dialog that offers a button the server will reject
 * is worse than one that explains why the button is not there.
 */
export const DeleteMethodRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
});
export type DeleteMethodRequest = z.infer<typeof DeleteMethodRequestSchema>;
export const DeleteMethodResponseSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  deletedAt: IsoDateTimeSchema,
  /** Graph elements that went with it, so the caller can say what was actually removed. */
  deletedElementCount: z.int().min(0),
  /**
   * True when this was the family's last version and the empty `Method` row went too.
   * Worth reporting: it is the difference between "that draft is gone" and "that method
   * is gone", and the author is entitled to know which one they just did.
   */
  deletedMethodFamily: z.boolean(),
});
export type DeleteMethodResponse = z.infer<typeof DeleteMethodResponseSchema>;

export const SubmitForReviewRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  note: DescriptionSchema.optional(),
});
export type SubmitForReviewRequest = z.infer<typeof SubmitForReviewRequestSchema>;
export const SubmitForReviewResponseSchema = z.strictObject({
  method: MethodVersionDetailSchema,
});
export type SubmitForReviewResponse = z.infer<typeof SubmitForReviewResponseSchema>;

export const ReviewMethodRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  verdict: ReviewVerdictSchema,
  comment: DescriptionSchema.optional(),
});
export type ReviewMethodRequest = z.infer<typeof ReviewMethodRequestSchema>;
export const ReviewMethodResponseSchema = z.strictObject({ method: MethodVersionDetailSchema });
export type ReviewMethodResponse = z.infer<typeof ReviewMethodResponseSchema>;

export const PublishMethodRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  /** Publishing refuses to proceed while error-severity diagnostics remain. */
  acknowledgeWarnings: z.boolean().optional(),
});
export type PublishMethodRequest = z.infer<typeof PublishMethodRequestSchema>;
export const PublishMethodResponseSchema = z.strictObject({
  method: MethodVersionDetailSchema,
  publishedAt: IsoDateTimeSchema,
  /** Byte-stable checksum of the published JSON-LD snapshot. */
  checksum: z.string().regex(/^[0-9a-f]{64}$/),
});
export type PublishMethodResponse = z.infer<typeof PublishMethodResponseSchema>;
